/**
 * Client for engine /v1/organization/settings.
 *
 * `pricing` is the org's Costing form; the engine layers its rates between the
 * template YAML and the machine park when it prices an RFQ line, and reads
 * overhead / default margin / rounding / validity from it. `pricing_effective`
 * is what a line is priced with right now and where each number came from.
 */
import { jsonFetch } from "./erpApi";

export type PricingRounding = "none" | "1" | "10" | "100";
export type PricingCurrency = "TRY" | "EUR" | "USD";

export const PRICING_RATE_KEYS = [
  "labor_try_per_hr",
  "machine_turning_try_per_hr",
  "machine_milling_try_per_hr",
  "machine_cutting_try_per_hr",
  "machine_drilling_try_per_hr",
  "machine_welding_try_per_hr",
  "machine_bending_try_per_hr",
] as const;
export type PricingRateKey = (typeof PRICING_RATE_KEYS)[number];

export interface OrgPricing extends Partial<Record<PricingRateKey, number | null>> {
  overhead_pct: number;
  scrap_pct: number;
  default_margin_pct: number;
  quote_validity_days: number;
  lead_buffer_days: number;
  rounding: PricingRounding;
  currency: PricingCurrency;
  default_steel_price_try_per_kg?: number | null;
}

export interface PricingEffective {
  rates: Record<string, number>;
  sources: Record<string, string>;
}

/**
 * White-label for the org subdomain (#85). Public: the engine serves it
 * unauthenticated from `GET /v1/branding` on `<org>.app…`.
 */
export interface OrgBrandingSettings {
  display_name: string | null;
  logo_url: string | null;
  /** `#rrggbb`, stored lowercase. */
  accent_color: string | null;
}

export interface OrgSettings {
  organization_id: string;
  organization_slug: string;
  zilliz_enabled: boolean;
  pricing: OrgPricing;
  pricing_effective: PricingEffective;
  /** Absent on engines older than the org-subdomain release. */
  branding?: OrgBrandingSettings;
}

export function fetchOrgSettings(): Promise<OrgSettings> {
  return jsonFetch("/v1/organization/settings");
}

/** Partial patch; `null` on a rate key clears the org override. */
export function patchOrgPricing(pricing: Partial<OrgPricing>): Promise<OrgSettings> {
  return jsonFetch("/v1/organization/settings", {
    method: "PATCH",
    body: JSON.stringify({ pricing }),
  });
}

/**
 * Owner-only branding patch. `null` clears a field; the engine answers 422
 * `invalid_settings` with a `branding.<field> …` message for a bad value.
 */
export function patchOrgBranding(branding: Partial<OrgBrandingSettings>): Promise<OrgSettings> {
  return jsonFetch("/v1/organization/settings", {
    method: "PATCH",
    body: JSON.stringify({ branding }),
  });
}
